import { useIntl } from "react-intl";
import {Modal, PopupProvider} from "../../UIElements";
import React, { useState, useRef } from "react";
import {useSupportBotContext} from "./SupportBotData";
import {useVersionDifferences, VersionDifferenceIndicator} from "./components/VersionDifferences";
import "./BotMaterials.scss";
import {File, Trash, Plus, AlertTriangle, Loader2, AlertCircle} from "lucide-react";
import { UploadFileToOpenAI, DeleteFileFromOpenAI } from "../../api/APIBot";
import { ImportRagFile, DeleteRagFile, CreateRagCorpus } from "../../api/APIGemini";
import { FILE_CONFIG, formatFileSize, validateFileType, getTotalFilesSize, migrateLegacyMaterial } from "../../utils";
import { useSnackbar } from "../../Content";
import { useCurrentUserData } from "../../App";

const BotMaterials = ({ botData, updateBotData }) => {
    const intl = useIntl();
    const { materials = [] } = botData;
    const fileInputRef = useRef(null);
    const { setSnackbarData } = useSnackbar();
    const { currentUserData } = useCurrentUserData();
    const [uploadingFiles, setUploadingFiles] = useState({});

    const { aiProvider, data: { supportBots } } = useSupportBotContext();
    const isGemini = aiProvider === "gemini";

    const migratedMaterials = materials.map(migrateLegacyMaterial);

    // OpenAI file hooks
    const uploadMutation = UploadFileToOpenAI();
    const deleteMutation = DeleteFileFromOpenAI();

    // Gemini file hooks
    const importRagFileMutation = ImportRagFile();
    const deleteRagFileMutation = DeleteRagFile();
    const createRagCorpusMutation = CreateRagCorpus();

    const {
        isBotFieldDifferent,
        getMissingMaterials,
        findDefaultBot
    } = useVersionDifferences();

    // Check if materials array differs from default
    const areMaterialsDifferent = isBotFieldDifferent(
        { config: botData },
        "config.materials"
    );

    const missingMaterials = getMissingMaterials({ config: botData });

    const isFileUsedInOtherVersions = (fileId) => {
        if (!fileId || !botData.versionGroup || !supportBots) {
            return false;
        }

        const botsInGroup = supportBots.filter(bot =>
            bot.config?.versionGroup === botData.versionGroup &&
            Number(bot.id) !== Number(botData.id)
        );

        // Check if other bots in version group uses this file ID
        return botsInGroup.some(bot =>
            Array.isArray(bot.config?.materials) &&
            bot.config.materials.some(material => {
                const materialId = typeof material === 'object' ? material.id : null;
                return materialId === fileId;
            })
        );
    };

    const [deleteConfirmation, setDeleteConfirmation] = useState({
        isOpen: false,
        index: null
    });
    const [isDeletingMaterial, setIsDeletingMaterial] = useState(false);

    const openDeleteConfirmation = (index) => {
        setDeleteConfirmation({
            isOpen: true,
            index
        });
    };

    const closeDeleteConfirmation = () => {
        setDeleteConfirmation({
            isOpen: false,
            index: null
        });
    };

    const removeMaterial = async () => {
        const { index } = deleteConfirmation;
        const materialToRemove = migratedMaterials[index];

        setIsDeletingMaterial(true);

        try {
            // Check if file is used in other versions
            const isSharedFile = materialToRemove.id && !materialToRemove.isLegacy &&
                                 isFileUsedInOtherVersions(materialToRemove.id);

            if (isSharedFile) {
                // Only remove from this version, not OpenAI
                const updatedMaterials = migratedMaterials.filter((_, i) => i !== index);
                updateBotData("materials", updatedMaterials);

                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({
                        id: 'file.removedFromVersion',
                        defaultMessage: 'File removed from this version'
                    }),
                    state: "success",
                    autoHide: true
                });
            } else {
                // File not shared, also delete from provider
                try {
                    if (materialToRemove.id && !materialToRemove.isLegacy) {
                        if (isGemini && materialToRemove.ragFileId && botData.ragCorpusName) {
                            await deleteRagFileMutation.mutateAsync({
                                corpusName: botData.ragCorpusName,
                                ragFileName: materialToRemove.ragFileId
                            });
                        } else if (!isGemini) {
                            await deleteMutation.mutateAsync(materialToRemove.id);
                        }
                    }

                    const updatedMaterials = migratedMaterials.filter((_, i) => i !== index);
                    updateBotData("materials", updatedMaterials);

                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage({
                            id: 'file.deleteSuccess',
                            defaultMessage: 'File deleted successfully'
                        }),
                        state: "success",
                        autoHide: true
                    });
                } catch (error) {
                    console.error('Delete error:', error);

                    if (error.response?.status === 404) {
                        // File doesn't exist, delete gracefully
                        const updatedMaterials = migratedMaterials.filter((_, i) => i !== index);
                        updateBotData("materials", updatedMaterials);

                        setSnackbarData({
                            show: true,
                            text: intl.formatMessage({
                                id: 'file.alreadyDeleted',
                                defaultMessage: 'File already deleted, removed from module'
                            }),
                            state: "success",
                            autoHide: true
                        });
                    } else {
                        setSnackbarData({
                            show: true,
                            text: intl.formatMessage({
                                id: 'file.deleteError',
                                defaultMessage: 'Failed to delete file from OpenAI'
                            }),
                            state: "error",
                            autoHide: true
                        });
                        setIsDeletingMaterial(false);
                        closeDeleteConfirmation();
                        return;
                    }
                }
            }
        } catch (error) {
            console.error('Unexpected error during material removal:', error);
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'file.deleteError',
                    defaultMessage: 'Failed to delete file from OpenAI'
                }),
                state: "error",
                autoHide: true
            });
        } finally {
            setIsDeletingMaterial(false);
            closeDeleteConfirmation();
        }
    };

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        // 1. PRE-VALIDATE ALL FILES BEFORE STARTING UPLOADS
        const validationErrors = [];
        let totalSize = getTotalFilesSize(migratedMaterials);

        for (const file of files) {
            // Validate individual file size
            if (file.size > FILE_CONFIG.MAX_FILE_SIZE) {
                validationErrors.push({
                    filename: file.name,
                    error: intl.formatMessage({
                        id: 'file.tooLarge',
                        defaultMessage: 'File is too large. Maximum size is {maxSize}.'
                    }, { maxSize: formatFileSize(FILE_CONFIG.MAX_FILE_SIZE) })
                });
                continue;
            }

            // Validate file type
            if (!validateFileType(file.name)) {
                validationErrors.push({
                    filename: file.name,
                    error: intl.formatMessage({
                        id: 'file.invalidType',
                        defaultMessage: 'Invalid file type. Allowed: PDF, DOCX, TXT, MD, JSON, CSV'
                    })
                });
                continue;
            }

            totalSize += file.size;
        }

        // Validate total size
        if (totalSize > FILE_CONFIG.MAX_TOTAL_SIZE) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'file.totalSizeExceeded',
                    defaultMessage: 'Total file size limit exceeded. Maximum total is {maxSize}.'
                }, { maxSize: formatFileSize(FILE_CONFIG.MAX_TOTAL_SIZE) }),
                state: "error",
                autoHide: true,
            });
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            return;
        }

        // Show validation errors if any
        if (validationErrors.length > 0) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'file.validationErrors',
                    defaultMessage: '{count} file(s) failed validation'
                }, { count: validationErrors.length }),
                state: "error",
                autoHide: true,
            });
            console.error('Validation errors:', validationErrors);
        }

        // Filter out files that failed validation
        const validFiles = files.filter(file =>
            !validationErrors.some(err => err.filename === file.name)
        );

        if (validFiles.length === 0) {
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            return;
        }

        // 2. INITIALIZE UPLOAD TRACKING
        const initialUploadState = {};
        validFiles.forEach(file => {
            initialUploadState[file.name] = { status: 'uploading', error: null };
        });
        setUploadingFiles(initialUploadState);

        // 3. UPLOAD FILES SEQUENTIALLY
        const results = { successful: [], failed: [] };
        const newMaterials = [...migratedMaterials];

        for (const file of validFiles) {
            try {
                let newMaterial;

                if (isGemini) {
                    // Gemini: file will be imported into RAG corpus at save time
                    // For now, store a reference with a temporary ID
                    const tempId = `pending_${Date.now()}_${file.name}`;
                    newMaterial = {
                        id: tempId,
                        filename: file.name,
                        size: file.size,
                        uploadedAt: new Date().toISOString(),
                        uploadedBy: currentUserData?.email || currentUserData?.uid || null,
                        purpose: 'rag',
                        mimeType: file.type || 'application/octet-stream',
                        // Store the file object for later upload during save
                        _pendingFile: file
                    };
                } else {
                    // OpenAI: Upload to files API
                    const uploadResult = await uploadMutation.mutateAsync({ file });
                    newMaterial = {
                        id: uploadResult.id,
                        filename: file.name,
                        size: file.size,
                        uploadedAt: new Date().toISOString(),
                        uploadedBy: currentUserData?.email || currentUserData?.uid || null,
                        purpose: 'assistants',
                        mimeType: file.type || 'application/octet-stream'
                    };
                }

                newMaterials.push(newMaterial);
                updateBotData("materials", newMaterials);

                // Update status to success
                setUploadingFiles(prev => ({
                    ...prev,
                    [file.name]: { status: 'success', error: null }
                }));

                results.successful.push(file.name);

            } catch (error) {
                console.error(`Upload error for ${file.name}:`, error);

                // Update status to error
                setUploadingFiles(prev => ({
                    ...prev,
                    [file.name]: { status: 'error', error: error.message || 'Upload failed' }
                }));

                results.failed.push(file.name);
            }
        }

        // 4. SHOW SUMMARY MESSAGE
        if (results.successful.length > 0 && results.failed.length === 0) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'file.uploadAllSuccess',
                    defaultMessage: '{count} file(s) uploaded successfully'
                }, { count: results.successful.length }),
                state: "success",
                autoHide: true,
            });
        } else if (results.successful.length > 0 && results.failed.length > 0) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'file.uploadPartialSuccess',
                    defaultMessage: '{success} succeeded, {failed} failed'
                }, { success: results.successful.length, failed: results.failed.length }),
                state: "warning",
                autoHide: true,
            });
        } else if (results.failed.length > 0) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'file.uploadAllFailed',
                    defaultMessage: 'All {count} file(s) failed to upload'
                }, { count: results.failed.length }),
                state: "error",
                autoHide: true,
            });
        }

        // 5. CLEANUP
        setTimeout(() => {
            setUploadingFiles({});
        }, 3000); // Clear upload status after 3 seconds

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Simplified to just trigger the file input
    const handleAddMaterial = () => {
        if (Object.keys(uploadingFiles).length > 0) return;
        fileInputRef.current.click();
    };

    // Add a missing material back to this version
    const handleAddMissingMaterial = (material) => {
        if (material && !migratedMaterials.some(m => m.filename === material || m.filename === material.filename)) {
            updateBotData("materials", [
                ...migratedMaterials,
                migrateLegacyMaterial(material)
            ]);
        }
    };

    // Calculate usage stats
    const totalSize = getTotalFilesSize(migratedMaterials);
    const fileCount = migratedMaterials.length;

    return (
        <PopupProvider>
            <h4 className="field-header">
                {intl.formatMessage({
                    id: 'referenceMaterials',
                    defaultMessage: 'Materials',
                })}
                {totalSize > 0 && (
                    <div className="materials-usage-info">
                        {formatFileSize(totalSize)} / {formatFileSize(FILE_CONFIG.MAX_TOTAL_SIZE)}
                    </div>
                )}
            </h4>

            <div className={"materials-list"}>
                {/* Placeholder when no materials */}
                {migratedMaterials.length === 0 && missingMaterials.length === 0 && (
                    <p className={"placeholder"}>
                        {intl.formatMessage({
                            id: 'materials.placeholder',
                            defaultMessage: 'The bot can use uploaded materials when forming responses to the student. Provide the bot with instructions on how to use the uploaded materials from the Instructions tab.'
                        })}
                    </p>
                )}

                {migratedMaterials.map((material, index) => {
                    // Get the default bot to check if this material exists there
                    const defaultBot = findDefaultBot({ config: botData });
                    const defaultMaterials = defaultBot?.config?.materials || [];

                    // Determine if this material is unique to this version (not in default)
                    const isMaterialUnique = defaultBot && !defaultMaterials.some(
                        m => (typeof m === 'string' ? m : m.filename) === material.filename
                    );

                    const differenceTitle = isMaterialUnique
                        ? intl.formatMessage({
                            id: 'bot.materials.difference.unique',
                            defaultMessage: 'Material added in this version (not in default)'
                        })
                        : "";

                    return (
                        <div key={index} className={`material-item ${isMaterialUnique ? 'version-different' : ''} ${material.isLegacy ? 'legacy-material' : ''}`}>
                            <File size={20} />
                            <div className="material-details">
                                <span className="material-name">
                                    {material.filename}
                                    {isMaterialUnique && (
                                        <VersionDifferenceIndicator differenceTitle={differenceTitle} />
                                    )}
                                    {material.isLegacy && (
                                        <span className="legacy-badge" title={intl.formatMessage({
                                            id: 'materials.legacy.title',
                                            defaultMessage: 'Legacy file - re-upload required'
                                        })}>
                                            <AlertCircle size={14} />{intl.formatMessage({
                                            id: 'materials.legacy.re-upload',
                                            defaultMessage: 'Re-upload required'
                                        })}
                                        </span>
                                    )}
                                </span>
                                {!material.isLegacy && (
                                    <span className="material-meta">
                                        {formatFileSize(material.size)}
                                        {material.uploadedAt && ` • ${intl.formatDate(new Date(material.uploadedAt), { dateStyle: 'medium' })}`}
                                        {material.uploadedBy && ` • ${material.uploadedBy}`}
                                    </span>
                                )}
                            </div>
                            <button
                                className={"remove-material"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteConfirmation(index);
                                }}
                                title={intl.formatMessage({
                                    id: 'survey.remove',
                                    defaultMessage: 'Remove',
                                })}
                            >
                                <Trash size={20} />
                            </button>
                        </div>
                    );
                })}

                {missingMaterials.length > 0 && (
                    <>
                        {missingMaterials.map((material, index) => {
                            const materialObj = migrateLegacyMaterial(material);
                            const differenceTitle = intl.formatMessage({
                                id: 'bot.materials.notIncluded',
                                defaultMessage: 'Material not included in this version'
                            });

                            return (
                                <div key={`missing-${index}`} className="material-item missing-material">
                                    <File size={20} />
                                    <div className="material-details">
                                        <span className="material-name">
                                            {materialObj.filename}
                                        </span>
                                    </div>
                                    <VersionDifferenceIndicator differenceTitle={differenceTitle} />
                                    <button
                                        className={"remove-material add-material"}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddMissingMaterial(material);
                                        }}
                                        title={intl.formatMessage({
                                            id: 'bot.materials.add',
                                            defaultMessage: 'Add to this version',
                                        })}
                                    >
                                        <Plus size={22} />
                                    </button>
                                </div>
                            );
                        })}
                    </>
                )}

                <button
                    className={`new-topic ${Object.keys(uploadingFiles).length > 0 ? 'uploading' : ''}`}
                    onClick={handleAddMaterial}
                    disabled={Object.keys(uploadingFiles).length > 0}
                >
                    {Object.keys(uploadingFiles).length > 0 ? (
                        <>
                            <Loader2 size={16} className="spinner" />
                            {intl.formatMessage({
                                id: 'uploading',
                                defaultMessage: 'Uploading...',
                            }, { count: Object.keys(uploadingFiles).length })}
                        </>
                    ) : (
                        <>
                            + {intl.formatMessage({
                                id: 'bot.materials.add',
                                defaultMessage: 'Add Material',
                            })}
                        </>
                    )}
                </button>
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                    accept={FILE_CONFIG.ALLOWED_EXTENSIONS.join(',')}
                    multiple
                />
            </div>

            {deleteConfirmation.isOpen && (
                <Modal
                    header={intl.formatMessage({
                        id: 'delete.material',
                        defaultMessage: 'Delete material',
                    })}
                    text={intl.formatMessage({
                        id: 'delete.material.text',
                        defaultMessage: 'Are you sure you want to delete this material? This will remove it from OpenAI.',
                    })}
                    closeModal={closeDeleteConfirmation}
                    confirmAction={removeMaterial}
                    confirmDisabled={isDeletingMaterial}
                    confirmText={isDeletingMaterial ? (
                        <>
                            <Loader2 size={16} className="spinner" />
                            {intl.formatMessage({
                                id: 'deleting',
                                defaultMessage: 'Deleting...',
                            })}
                        </>
                    ) : (
                        intl.formatMessage({
                            id: 'survey.delete',
                            defaultMessage: 'Delete',
                        })
                    )}
                    alert={true}
                />
            )}
        </PopupProvider>
    );
};

export default BotMaterials;
